import { MakeLogicType, actions, connect, kea, listeners, path, reducers, selectors } from 'kea'
import { lazyLoaders, loaders } from 'kea-loaders'

import api, { PaginatedResponse } from 'lib/api'
import { timeSensitiveAuthenticationLogic } from 'lib/components/TimeSensitiveAuthentication/timeSensitiveAuthenticationLogic'
import { OrganizationMembershipLevel } from 'lib/constants'
import { lemonToast } from 'lib/lemon-ui/LemonToast/LemonToast'
import { bindModalToUrl } from 'lib/logic/bindModalToUrl'
import { pluralize } from 'lib/utils/strings'
import { organizationLogic } from 'scenes/organizationLogic'
import { preflightLogic } from 'scenes/PreflightCheck/preflightLogic'

import { AccessControlLevel, OrganizationInviteType } from '~/types'

import type { PreflightStatus } from '../../../types'

/** State of a single invite row (with input data) in bulk invite creation. */
export interface InviteRowState {
    target_email: string
    first_name: string
    level: OrganizationMembershipLevel
    isValid: boolean
    message?: string
    private_project_access: Array<{ id: number; level: AccessControlLevel.Member | AccessControlLevel.Admin }>
}

const EMPTY_INVITE: InviteRowState = {
    target_email: '',
    first_name: '',
    level: OrganizationMembershipLevel.Member,
    isValid: true,
    private_project_access: [],
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface inviteLogicValues {
    preflight: PreflightStatus | null // preflightLogic
    availableProjects: any[]
    canSubmit: boolean
    inviteContainsOwnerLevel: boolean
    invitedTeamMembersInternal: OrganizationInviteType[]
    invitedTeamMembersInternalLoading: boolean
    invites: OrganizationInviteType[]
    invitesLoading: boolean
    invitesToSend: InviteRowState[]
    isInviteConfirmed: boolean
    isInviteModalShown: boolean
    isInviting: boolean
    message: string
    projectAccessControls: Record<
        number,
        {
            access_level: string
        }
    >
    projectAccessControlsLoading: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface inviteLogicActions {
    addProjectAccess: (
        inviteIndex: number,
        projectId: number,
        level: AccessControlLevel
    ) => {
        inviteIndex: number
        level: AccessControlLevel
        projectId: number
    }
    appendInviteRow: () => {
        value: true
    }
    deleteInvite: (invite: OrganizationInviteType) => OrganizationInviteType
    deleteInviteAtIndex: (index: number) => {
        index: number
    }
    deleteInviteFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    deleteInviteSuccess: (
        invites: OrganizationInviteType[],
        payload?: OrganizationInviteType
    ) => {
        invites: OrganizationInviteType[]
        payload?: OrganizationInviteType
    }
    hideInviteModal: () => {
        value: true
    }
    inviteTeamMembers: () => any
    inviteTeamMembersFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    inviteTeamMembersSuccess: (
        invitedTeamMembersInternal: OrganizationInviteType[],
        payload?: any
    ) => {
        invitedTeamMembersInternal: OrganizationInviteType[]
        payload?: any
    }
    loadInvites: () => any
    loadInvitesFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadInvitesSuccess: (
        invites: OrganizationInviteType[],
        payload?: any
    ) => {
        invites: OrganizationInviteType[]
        payload?: any
    }
    loadProjectAccessControl: (projectId: number) => number
    loadProjectAccessControlFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadProjectAccessControlSuccess: (
        projectAccessControls: {
            [x: number]: any
        },
        payload?: number
    ) => {
        projectAccessControls: {
            [x: number]: any
        }
        payload?: number
    }
    removeProjectAccess: (
        inviteIndex: number,
        projectId: number
    ) => {
        inviteIndex: number
        projectId: number
    }
    resetInviteRows: () => {
        value: true
    }
    setIsInviteConfirmed: (inviteConfirmed: boolean) => {
        inviteConfirmed: boolean
    }
    showInviteModal: () => {
        value: true
    }
    updateInviteAtIndex: (
        payload: any,
        index: number
    ) => {
        index: number
        payload: any
    }
    updateMessage: (message: string) => {
        message: string
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface inviteLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        inviteContainsOwnerLevel: (invitesToSend: InviteRowState[]) => boolean
        canSubmit: (
            invitesToSend: InviteRowState[],
            inviteContainsOwnerLevel: boolean,
            isInviteConfirmed: boolean
        ) => boolean
        availableProjects: (currentOrganization: null | import('~/types').OrganizationType) => any[]
        isInviting: (invitedTeamMembersInternalLoading: boolean) => boolean
    }
}

export type inviteLogicType = MakeLogicType<inviteLogicValues, inviteLogicActions, Record<string, any>, inviteLogicMeta>

export const inviteLogic = kea<inviteLogicType>([
    path(['scenes', 'organization', 'Settings', 'inviteLogic']),
    connect(() => ({
        values: [preflightLogic, ['preflight']],
    })),
    actions({
        showInviteModal: true,
        hideInviteModal: true,
        updateInviteAtIndex: (payload, index: number) => ({ payload, index }),
        deleteInviteAtIndex: (index: number) => ({ index }),
        updateMessage: (message: string) => ({ message }),
        appendInviteRow: true,
        resetInviteRows: true,
        setIsInviteConfirmed: (inviteConfirmed: boolean) => ({ inviteConfirmed }),
        addProjectAccess: (inviteIndex: number, projectId: number, level: AccessControlLevel) => ({
            inviteIndex,
            projectId,
            level,
        }),
        removeProjectAccess: (inviteIndex: number, projectId: number) => ({ inviteIndex, projectId }),
    }),
    loaders(({ values }) => ({
        invitedTeamMembersInternal: [
            [] as OrganizationInviteType[],
            {
                inviteTeamMembers: async () => {
                    if (!values.canSubmit) {
                        return []
                    }

                    const payload: Pick<
                        OrganizationInviteType,
                        'target_email' | 'first_name' | 'level' | 'message' | 'private_project_access'
                    >[] = values.invitesToSend.filter((invite) => invite.target_email)
                    if (values.message) {
                        payload.forEach((payload) => (payload.message = values.message))
                    }
                    // Inviting members is a sensitive action; if re-authentication is required,
                    // await its completion so the invite resumes once the user re-authenticates.
                    await timeSensitiveAuthenticationLogic.findMounted()?.asyncActions.checkReauthentication()
                    return await api.create<OrganizationInviteType[]>(
                        `api/organizations/${organizationLogic.values.currentOrganizationId}/invites/bulk/`,
                        payload
                    )
                },
            },
        ],
        projectAccessControls: [
            {} as Record<number, { access_level: string }>,
            {
                loadProjectAccessControl: async (projectId: number) => {
                    try {
                        const accessControls = await api.get(`api/projects/${projectId}/access_controls`)
                        // Look for project-level access control (resource: "project", organization_member: null, role: null)
                        const projectAccessControl = accessControls.access_controls?.find(
                            (control: any) =>
                                control.resource === 'project' &&
                                control.organization_member === null &&
                                control.role === null
                        )
                        return {
                            ...values.projectAccessControls,
                            [projectId]: projectAccessControl || { access_level: accessControls.default_access_level },
                        }
                    } catch {
                        return values.projectAccessControls
                    }
                },
            },
        ],
    })),
    lazyLoaders(({ values }) => ({
        invites: [
            [] as OrganizationInviteType[],
            {
                loadInvites: async () => {
                    return organizationLogic.values.currentOrganization
                        ? (
                              await api.get<PaginatedResponse<OrganizationInviteType>>(
                                  `api/organizations/${organizationLogic.values.currentOrganizationId}/invites/`
                              )
                          ).results
                        : []
                },
                deleteInvite: async (invite: OrganizationInviteType) => {
                    await api.delete(
                        `api/organizations/${organizationLogic.values.currentOrganizationId}/invites/${invite.id}/`
                    )
                    preflightLogic.actions.loadPreflight() // Make sure licensed_users_available is updated
                    lemonToast.success(`Invite for ${invite.target_email} has been canceled`)
                    return values.invites.filter((thisInvite) => thisInvite.id !== invite.id)
                },
            },
        ],
    })),
    reducers(() => ({
        isInviteModalShown: [
            false,
            {
                showInviteModal: () => true,
                hideInviteModal: () => false,
            },
        ],
        invitesToSend: [
            [EMPTY_INVITE] as InviteRowState[],
            {
                updateInviteAtIndex: (state, { payload, index }) => {
                    const newState = [...state]
                    newState[index] = { ...state[index], ...payload }
                    return newState
                },
                deleteInviteAtIndex: (state, { index }) => {
                    const newState = [...state]
                    newState.splice(index, 1)
                    return newState
                },
                appendInviteRow: (state) => [...state, EMPTY_INVITE],
                resetInviteRows: () => [EMPTY_INVITE],
                inviteTeamMembersSuccess: () => [EMPTY_INVITE],
                addProjectAccess: (state, { inviteIndex, projectId, level }) => {
                    const newState = [...state]
                    const invite = { ...newState[inviteIndex] }

                    // Remove existing access for this project if it exists
                    invite.private_project_access = invite.private_project_access.filter(
                        (access) => access.id !== projectId
                    )

                    // Add new access
                    invite.private_project_access.push({
                        id: projectId,
                        level: level as AccessControlLevel.Member | AccessControlLevel.Admin,
                    })
                    newState[inviteIndex] = invite
                    return newState
                },
                removeProjectAccess: (state, { inviteIndex, projectId }) => {
                    const newState = [...state]
                    const invite = { ...newState[inviteIndex] }
                    invite.private_project_access = invite.private_project_access.filter(
                        (access) => access.id !== projectId
                    )
                    newState[inviteIndex] = invite
                    return newState
                },
            },
        ],
        message: [
            '',
            {
                updateMessage: (_, { message }) => message,
            },
        ],
        isInviteConfirmed: [
            false,
            {
                setIsInviteConfirmed: (_, { inviteConfirmed }) => inviteConfirmed,
            },
        ],
    })),
    selectors({
        inviteContainsOwnerLevel: [
            (selectors) => [selectors.invitesToSend],
            (invites: InviteRowState[]) => {
                return invites.filter(({ level }) => level === OrganizationMembershipLevel.Owner).length > 0
            },
        ],
        canSubmit: [
            (selectors) => [selectors.invitesToSend, selectors.inviteContainsOwnerLevel, selectors.isInviteConfirmed],
            (invites: InviteRowState[], inviteContainsOwnerLevel: boolean, isInviteConfirmed: boolean) => {
                const ownerLevelConfirmed = inviteContainsOwnerLevel ? isInviteConfirmed : true
                return (
                    invites.filter(({ target_email }) => !!target_email).length > 0 &&
                    invites.filter(({ isValid }) => !isValid).length == 0 &&
                    ownerLevelConfirmed
                )
            },
        ],
        availableProjects: [
            () => [organizationLogic.selectors.currentOrganization],
            (currentOrganization: any) => {
                return [...(currentOrganization?.teams || [])].sort((a, b) => a.name.localeCompare(b.name))
            },
        ],
        isInviting: [
            (selectors) => [selectors.invitedTeamMembersInternalLoading],
            (invitedTeamMembersInternalLoading: boolean) => invitedTeamMembersInternalLoading,
        ],
    }),
    listeners(({ values, actions }) => ({
        inviteTeamMembersSuccess: (): void => {
            const inviteCount = values.invitedTeamMembersInternal.length
            if (values.preflight?.email_service_available) {
                lemonToast.success(`Invited ${pluralize(inviteCount, 'new team member')}`)
            } else {
                lemonToast.success('Team invite links generated')
            }

            organizationLogic.actions.loadCurrentOrganization()
            actions.loadInvites()

            if (values.preflight?.email_service_available) {
                actions.hideInviteModal()
            }
        },
        addProjectAccess: ({ projectId }) => {
            // Load access control for the project when it's added
            actions.loadProjectAccessControl(projectId)
        },
    })),
    bindModalToUrl({
        urlKey: 'invite-members',
        openActionKey: 'showInviteModal',
        closeActionKey: 'hideInviteModal',
        isOpenKey: 'isInviteModalShown',
    }),
])
